import { useEffect } from 'react';
import { Routes, Route, Navigate, Outlet, useLocation } from 'react-router-dom'; // useLocationを追加
import { useAuth } from '../contexts/AuthContext';
import Login from '../pages/Login/Login';
import { CommonLayout } from '../components/common/CommonLayout';
import { AdminProtectedRoute } from './AdminProtectedRoute';
import type { JSX } from 'react';

// === ページコンポーネント ===
import MenuPage from '../pages/Menu/MenuPage';
import { BiddingItemMaintenancePage } from '../pages/admin/BiddingItemMaintenance/BiddingItemMaintenancePage';
import BiddingInputPage from '../pages/buyer/BiddingInput/BiddingInputPage';
import BiddingInputPage2 from '../pages/buyer/BiddingInput/BiddingInputPage2';
import BiddingInputNumberPage from '../pages/buyer/BiddingInput/BiddingInputNumberPage';
import { BiddingResultPage } from '../pages/admin/BiddingResult/BiddingResultPage';
import BiddingItemStatusUpdateEachUserPage from '../pages/admin/BiddingItemStatusUpdate/BiddingItemStatusUpdateEachUserPage';
import BiddingItemUploadPage from '../pages/admin/BiddingItemUpload/BiddingItemUploadPage';
import MasterReferencePage from '../pages/admin/MasterReference/MasterReferencePage';
import ListingSchedulePage from '../pages/ListingSchedule/ListingSchedulePage';
import { BiddingConfirmationPage } from '../pages/admin/BiddingConfirmation/BiddingConfirmationPage';
import { ListingPage } from '../pages/Listing/ListingPage';
import { ReportConst } from '../constants/ReportConst';
import { ManualNumberingPage } from '../pages/admin/ManualNumbering/ManualNumberingPage'
import { CSVOutputPage } from '../pages/admin/CSVOutput/CSVOutputPage'

import UserMaintenancePage from '../pages/admin/UserMaintenance/UserMaintenancePage';
import { BiddingMonitorPage } from '../pages/admin/BiddingMonitor/BiddingMonitorPage';
import BiddingMinPriceSetting from '../pages/BiddingMinPriceSetting/BiddingMinPriceSetting';
import BiddingSchedulePage from '../pages/admin/BiddingSchedule/BiddingSchedulePage';
import NoticeManagement from '../pages/admin/NoticeManagement/NoticeManagement';
import MarketResultCsvPage from '../pages/admin/MarketResultCsv/MarketResultCsvPage';

const ScrollToTop = () => {
  const { pathname } = useLocation();

  useEffect(() => {
    // 画面全体のスクロールをリセット
    window.scrollTo(0, 0);
    // メインエリアのスクロールもリセット
    document.querySelector('.main-container')?.scrollTo(0, 0);
  }, [pathname]);

  return null;
};


const ProtectedRoute = ({ children }: { children: JSX.Element }) => {
  const { currentUser, isLoading } = useAuth();
  
  if (isLoading) {
    return <div className="flex justify-center items-center h-screen">Loading...</div>;
  }

  return currentUser ? children : <Navigate to="/login" />;
};

export const AppRouter = () => {
  return (
    <>
      <ScrollToTop /> 

      <Routes>
        <Route path="/login" element={<Login />} />
        
        <Route 
          path="/" 
          element={
            <ProtectedRoute>
              <CommonLayout />
            </ProtectedRoute>
          }
        >
          {/* トップ画面 */}
          <Route index element={<MenuPage />} />

          {/* 買い手機能 */}
          <Route path="buyer">
             <Route path="input" element={<BiddingInputPage />} />
             <Route path="input2" element={<BiddingInputPage2 />} />
             <Route path="input-number" element={<BiddingInputNumberPage/>} />
          </Route>

          {/* 出品メンテ */}
          <Route path="maintenance" element={<BiddingItemMaintenancePage />} />
          
          {/* 出品メンテ(一括登録) */}
          <Route path="itemBulkUpload" element={<BiddingItemUploadPage />} />

          {/* 最低落札値設定画面 */}
          <Route path="bid-min-price-setting" element={<BiddingMinPriceSetting />} />

          {/* 入札結果確認 */}
          <Route path="results" element={<BiddingResultPage />} />

          {/* 出品ステータス変更機能 */}
          <Route path="updatestatus-eachuser" element={<BiddingItemStatusUpdateEachUserPage/>}/>

          {/* 出品予定確認 */}
          <Route path="listing-schedule" element={<ListingSchedulePage />} />
             
          {/* 市場用スケジュール設定画面 */}
          <Route path="bidding-schedule" element={<BiddingSchedulePage />} />

          {/* 市場専用機能 */}
          <Route path="admin" element={
            <AdminProtectedRoute>
              <Outlet />
            </AdminProtectedRoute>
          }> 
             <Route index element={<Navigate to="/" replace />} />           
             
             {/* 出品一覧表印刷 */} 
             <Route path="print-list" element={<ListingPage reportType={ReportConst.SYUKKO_ICHIRAN} title="出品一覧表印刷" />} />
             {/* 手合票印刷 */}
             <Route path="print-deal" element={<ListingPage reportType={ReportConst.TEAWASE} title="手合票印刷" />} />

             {/* マスタ情報照会 (URL: /admin/masters) */}
             <Route path="masters" element={<MasterReferencePage />} />
             
             {/* ユーザーメンテ機能 (URL: /admin/users) */}
             <Route path="users/Maintenance" element={<UserMaintenancePage />} />

             {/* 落札結果一覧表 */}
             <Route path="inquiry/win-list" element={<ListingPage reportType={ReportConst.RAKUSATSU_ICHIRAN} title="落札結果一覧表(公開用)" />} />

             {/* 入札状況確認 */}
             <Route path="bidding-monitor" element={<BiddingMonitorPage/>} />

             {/* 入札Noの手動採番 */}
             <Route path="manual-numbering" element={<ManualNumberingPage/>} />

             {/* 落札確定機能 */}
             <Route path="bid-confirmation" element={<BiddingConfirmationPage/>} />

             {/* 連携用CSV再出力 */}
             <Route path="csv-reexport" element={<CSVOutputPage/>} />

             {/* 落札結果CSV出力（期間指定） */}
             <Route path="result-csv-range" element={<MarketResultCsvPage/>} />

             {/* お知らせメンテナンス */}
             <Route path="notice-management" element={<NoticeManagement/>} />

          </Route>

        </Route>
        
        <Route path="*" element={<Navigate to="/" />} />
      </Routes>
    
    </> 
  );
};